import React, { useState, useEffect } from 'react';
import JsonEditor from './JsonEditor';
import { get as apiGet, post as apiPost, put as apiPut, del as apiDel } from '../../api/apiClient';

const ApiTester = ({ endpoint }) => {
  const [expanded, setExpanded] = useState(false);
  const [requestData, setRequestData] = useState(endpoint.sampleBody || {});
  const [queryParams, setQueryParams] = useState(endpoint.sampleParams || {});
  const [responseData, setResponseData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [pathParams, setPathParams] = useState({});
  const [showResponse, setShowResponse] = useState(false);
  const [authError, setAuthError] = useState(null);

  // Reset form when endpoint changes
  useEffect(() => {
    setRequestData(endpoint.sampleBody || {});
    setQueryParams(endpoint.sampleParams || {});
    setPathParams({});
    setResponseData(null);
    setError(null);
    setShowResponse(false);
  }, [endpoint]);

  // Extract path parameters from endpoint path (e.g., /users/update/:userId)
  useEffect(() => {
    const params = {};
    const pathParamRegex = /:([^/]+)/g;
    let match;

    while ((match = pathParamRegex.exec(endpoint.path)) !== null) {
      params[match[1]] = '';
    }

    setPathParams(params);
  }, [endpoint.path]);

  // Build final path with path parameters replaced
  const getFullPath = () => {
    let path = endpoint.path;
    Object.entries(pathParams).forEach(([key, value]) => {
      path = path.replace(`:${key}`, value || `:${key}`);
    });
    return path;
  };

  // Build URL with query parameters
  const getUrlWithParams = basePath => {
    const apiPath = basePath.startsWith('/api/') ? basePath : `/api${basePath}`;
    const url = new URL(`${window.location.origin}${apiPath}`);
    Object.entries(queryParams).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        url.searchParams.append(key, value);
      }
    });
    // Return path with query only (apiClient will resolve base URL)
    return url.pathname + url.search;
  };

  const handleSubmit = async e => {
    e.preventDefault();

    setLoading(true);
    setError(null);
    setAuthError(null);
    setShowResponse(true);

    try {
      const path = getFullPath();

      // Build path with query parameters for GET requests
      let fullUrl = path;
      if (endpoint.method === 'GET' && Object.keys(queryParams).length > 0) {
        const url = new URL(`${window.location.origin}${path}`);
        Object.entries(queryParams).forEach(([key, value]) => {
          if (value) {
            url.searchParams.append(key, value);
          }
        });
        // Extract just the path with query parameters
        fullUrl = url.pathname + url.search;
      }

      // Handle different HTTP methods
      let response;
      switch (endpoint.method) {
        case 'GET':
          response = await apiGet(fullUrl);
          break;
        case 'POST':
          response = await apiPost(path, requestData);
          break;
        case 'PUT':
          response = await apiPut(path, requestData);
          break;
        case 'DELETE':
          response = await apiDel(path);
          break;
        default:
          // Fallback: treat as POST-like with body if provided
          response = await apiPost(path, requestData);
      }

      setResponseData(response);
    } catch (err) {
      console.error('Backend API request failed:', err);

      // Handle authentication specific errors (Firebase)
      if (err.response?.status === 401 || err.response?.status === 403) {
        setAuthError('Authentication error (Firebase token required or insufficient role)');
      }

      setError(err.message || 'Failed to make API request');
      setResponseData(err.response || null);
    } finally {
      setLoading(false);
    }
  };

  const handleRequestDataChange = newData => {
    try {
      setRequestData(newData);
    } catch (err) {
      console.error('Invalid JSON:', err);
    }
  };

  const handleQueryParamChange = (key, value) => {
    setQueryParams(prev => ({
      ...prev,
      [key]: value,
    }));
  };

  const handlePathParamChange = (key, value) => {
    setPathParams(prev => ({
      ...prev,
      [key]: value,
    }));
  };

  return (
    <div className="mb-6 border border-gray-200 rounded-lg overflow-hidden bg-white">
      {/* Endpoint Header */}
      <div
        className="flex justify-between items-center p-4 cursor-pointer bg-gray-50"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-center">
          <span
            className={`
            px-2 py-1 mr-3 rounded text-xs font-bold 
            ${
              endpoint.method === 'GET'
                ? 'bg-blue-100 text-blue-800'
                : endpoint.method === 'POST'
                  ? 'bg-green-100 text-green-800'
                  : endpoint.method === 'PUT'
                    ? 'bg-yellow-100 text-yellow-800'
                    : endpoint.method === 'DELETE'
                      ? 'bg-red-100 text-red-800'
                      : 'bg-gray-100 text-gray-800'
            }
          `}
          >
            {endpoint.method}
          </span>
          <span className="font-mono text-sm">{endpoint.path}</span>
        </div>
        <div className="flex items-center space-x-3">
          <span className="text-xs px-2 py-1 bg-gray-100 rounded">
            Role: {endpoint.requiredRole}
          </span>
          <svg
            className={`w-5 h-5 transform transition-transform ${expanded ? 'rotate-180' : ''}`}
            fill="currentColor"
            viewBox="0 0 20 20"
          >
            <path
              fillRule="evenodd"
              d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
              clipRule="evenodd"
            />
          </svg>
        </div>
      </div>

      {/* Endpoint Details */}
      {expanded && (
        <div className="p-4 border-t border-gray-200">
          <p className="text-sm text-gray-600 mb-4">{endpoint.description}</p>

          <form onSubmit={handleSubmit}>
            {/* Path Parameters */}
            {Object.keys(pathParams).length > 0 && (
              <div className="mb-4">
                <h4 className="text-sm font-semibold mb-2">Path Parameters</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {Object.entries(pathParams).map(([key, value]) => (
                    <div key={key} className="flex items-center">
                      <label className="w-1/3 text-sm text-gray-600">{key}:</label>
                      <input
                        type="text"
                        value={value}
                        onChange={e => handlePathParamChange(key, e.target.value)}
                        placeholder={key}
                        className="flex-1 p-2 border border-gray-300 rounded text-sm"
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Query Parameters */}
            {endpoint.queryParams && endpoint.queryParams.length > 0 && (
              <div className="mb-4">
                <h4 className="text-sm font-semibold mb-2">Query Parameters</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {endpoint.queryParams.map(param => (
                    <div key={param.name} className="flex items-center">
                      <label className="w-1/3 text-sm text-gray-600">
                        {param.name}
                        {param.required && <span className="text-red-500 ml-1">*</span>}:
                      </label>
                      <input
                        type="text"
                        value={queryParams[param.name] || ''}
                        onChange={e => handleQueryParamChange(param.name, e.target.value)}
                        placeholder={param.description}
                        className="flex-1 p-2 border border-gray-300 rounded text-sm"
                        required={param.required}
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Request Body for non-GET requests */}
            {endpoint.method !== 'GET' && endpoint.requestBody && (
              <div className="mb-4">
                <h4 className="text-sm font-semibold mb-2">Request Body</h4>
                <JsonEditor value={requestData} onChange={handleRequestDataChange} height="150px" />
              </div>
            )}

            <div className="flex justify-between mt-4">
              <button
                type="submit"
                disabled={loading}
                className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 disabled:opacity-50"
              >
                {loading ? 'Sending...' : 'Send Request'}
              </button>
              {showResponse && (
                <button
                  type="button"
                  onClick={() => setShowResponse(false)}
                  className="px-4 py-2 text-gray-600 rounded hover:bg-gray-100"
                >
                  Hide Response
                </button>
              )}
            </div>
          </form>

          {/* Response Data */}
          {showResponse && (
            <div className="mt-6">
              <h4 className="text-sm font-semibold mb-2">Response</h4>
              {loading ? (
                <div className="text-center p-4">
                  <svg
                    className="animate-spin h-5 w-5 mx-auto"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                </div>
              ) : (
                <div>
                  {authError ? (
                    <div className="p-4 bg-yellow-50 border border-yellow-200 rounded mb-4">
                      <p className="text-yellow-700 font-semibold text-sm">Authentication Error</p>
                      <p className="text-yellow-600 text-sm">{authError}</p>
                    </div>
                  ) : null}

                  {error ? (
                    <div className="p-4 bg-red-50 border border-red-200 rounded">
                      <p className="text-red-500 text-sm">{error}</p>
                      {responseData && (
                        <pre className="mt-2 bg-gray-50 p-3 rounded text-xs overflow-auto max-h-64">
                          {JSON.stringify(responseData, null, 2)}
                        </pre>
                      )}
                    </div>
                  ) : responseData ? (
                    <pre className="bg-gray-50 p-4 rounded text-xs overflow-auto max-h-64">
                      {JSON.stringify(responseData, null, 2)}
                    </pre>
                  ) : (
                    <p className="text-gray-500 text-sm italic">
                      No response yet. Send a request to see results.
                    </p>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ApiTester;
